import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useAuth, useCachedFetch } from '@structural-climbing/auth'
import { Button, softBadgeColors, Spinner } from '@structural-climbing/ui'
import {
  disciplineForSection,
  gradeDisplay,
  type Discipline,
} from '@structural-climbing/validation'
import { Sheet } from '../../design/Sheet'
import { ChipTabs } from '../../design/ChipTabs'
import { useGymGradeConfig } from '../gym/useGymGradeConfig'
import { disciplinePath } from '../nav/navGroups'
import type { GymRoute } from '../routes/useGymRoutes'

/** Same rule as the NOU badge on the wall: set in the last three days. */
function isNewRoute(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < 3 * 24 * 60 * 60 * 1000
}

/** Cleared by `invalidateAfterAscent`, so a logged route leaves "unclimbed". */
export const PICKER_ROUTES_CACHE_PREFIX = 'unclimbed-routes:'

interface Props {
  isOpen: boolean
  onClose: () => void
  gymId: string
  gymSlug: string
  /** The gym's walls. Two of them get a switch between bouldering and lead. */
  disciplines: readonly Discipline[]
  /** The wall to open on: the one whose session card was tapped. */
  initialDiscipline?: Discipline
  /** Opens the route's own sheet, the same one the wall uses to log. */
  onPick: (route: GymRoute) => void
}

/**
 * "Log TOP" from Home: find the route, then log it in the route's own sheet.
 *
 * Only the finding happens here. Logging (attempts, rating, a comment for the
 * setters) is the wall's route sheet, opened by Home, so a top logged from
 * Home and one logged from the wall are the same thing.
 *
 * A gym with both bouldering and lead gets a switch between the two. Each
 * discipline's routes are asked for once, the first time its tab is opened.
 */
export function QuickTopSheet({
  isOpen,
  onClose,
  gymId,
  gymSlug,
  disciplines,
  initialDiscipline,
  onPick,
}: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const gradeConfig = useGymGradeConfig()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [discipline, setDiscipline] = useState<Discipline>(
    initialDiscipline ?? disciplines[0] ?? 'boulder',
  )
  const [choice, setShow] = useState<'new' | 'unclimbed' | null>(null)

  // Every active route on this wall, with whether you have topped it. One
  // request answers both tabs.
  const { data: wall, loading } = useCachedFetch<GymRoute[]>(
    isOpen ? `${PICKER_ROUTES_CACHE_PREFIX}${gymId}:${discipline}` : null,
    async () => {
      const token = await getAccessTokenSilently()
      const params = new URLSearchParams({
        status: 'active',
        gym_id: gymId,
        discipline,
        limit: '100',
      })
      const res = await fetch(`${apiUrl}/my-gym-routes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load routes')
      const body = (await res.json()) as { routes: GymRoute[] } | GymRoute[]
      return Array.isArray(body) ? body : body.routes
    },
  )

  const routes = wall ?? []
  const newRoutes = routes.filter((r) => isNewRoute(r.created_at))
  // Open on the new ones when there are any; otherwise straight to the rest.
  const show = choice ?? (wall && newRoutes.length === 0 ? 'unclimbed' : 'new')
  const list = show === 'new' ? newRoutes : routes.filter((r) => !r.has_ascent)

  const close = () => {
    setShow(null)
    onClose()
  }

  return (
    <Sheet isOpen={isOpen} onClose={close} aria-label={t('session.pickTitle')}>
      <div className="flex min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain px-4 pb-6 pt-1">
        <h2 className="text-xl font-bold tracking-tight">{t('session.pickTitle')}</h2>

        {disciplines.length > 1 && (
          <ChipTabs
            aria-label={t('routes.discipline')}
            value={discipline}
            onChange={(d) => {
              setDiscipline(d)
              setShow(null)
            }}
            options={disciplines.map((d) => ({
              key: d,
              label: d === 'lead' ? t('routes.disciplineLead') : t('routes.disciplineBouldering'),
            }))}
          />
        )}

        <ChipTabs
          aria-label={t('session.pickTitle')}
          value={show}
          onChange={setShow}
          options={[
            { key: 'new', label: t('session.pickNew') },
            { key: 'unclimbed', label: t('session.pickUnclimbed') },
          ]}
        />

        {loading && !wall ? (
          <div className="flex justify-center py-6">
            <Spinner size="sm" color="primary" />
          </div>
        ) : list.length === 0 ? (
          <p className="text-sm text-foreground/55">
            {show === 'new' ? t('session.noNewRoutes') : t('session.allTried')}
          </p>
        ) : (
          <div role="list" className="flex flex-col gap-1.5">
            {list.map((route) => {
              const grade = gradeDisplay(
                gradeConfig,
                route.grade,
                disciplineForSection(route.section?.discipline),
              )
              const badge = softBadgeColors(grade.color)
              return (
                <Button
                  key={route.id}
                  role="listitem"
                  variant="bordered"
                  className="h-auto justify-start gap-2 border-[var(--gym-line)] px-3 py-2.5"
                  onPress={() => {
                    setShow(null)
                    onPick(route)
                  }}
                >
                  {route.image_r2_key && (
                    <img
                      src={`${apiUrl}/climbing-routes/${route.id}/image?size=thumb`}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-md object-cover"
                      loading="lazy"
                    />
                  )}
                  <span
                    className="h-4 w-4 shrink-0 rounded-full border border-foreground/10"
                    style={{ backgroundColor: route.color ?? '#9ca3af' }}
                  />
                  <span
                    className="rounded px-2 py-0.5 text-sm font-semibold"
                    style={{ backgroundColor: badge.bg, color: badge.fg }}
                  >
                    {grade.label}
                  </span>
                  {route.section?.name && (
                    <span className="truncate text-sm text-foreground/55">
                      {route.section.name}
                    </span>
                  )}
                  {route.has_ascent && (
                    <span className="ml-auto shrink-0 text-xs font-semibold text-success">
                      ✓ TOP
                    </span>
                  )}
                </Button>
              )
            })}
          </div>
        )}

        <Link
          to={`/${gymSlug}/${disciplinePath(discipline)}/routes`}
          onClick={close}
          className="text-sm font-semibold text-[var(--gym-primary)]"
        >
          {t('session.pickOnWall')} →
        </Link>
      </div>
    </Sheet>
  )
}
